import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableColumn, Button } from '@/components/common';
import { TransactionRecord } from '@/types/customer';
import { Customer } from '@/types/customer';
import { formatCurrency } from '@/utils/number-format';
import DateRangePicker from './DateRangePicker';
import { customerApi } from '@/api/endpoints/customers';
import { CreateReceiptVoucherModal } from '@/components/receiptVouchers';
import CreateDiscountVoucherModal from '@/components/discountVouchers/modals/CreateDiscountVoucherModal';
import QuickConfirmReceiptVoucherModal from '@/components/receiptVouchers/modals/QuickConfirmReceiptVoucherModal';
import ConfirmDiscountVoucherModal from '@/components/discountVouchers/modals/ConfirmDiscountVoucherModal';
import CreateDebtAdjustmentVoucherModal from '@/components/debtAdjustmentVouchers/modals/CreateDebtAdjustmentVoucherModal';
import ConfirmDebtAdjustmentVoucherModal from '@/components/debtAdjustmentVouchers/modals/ConfirmDebtAdjustmentVoucherModal';
import DebtAdjustmentVoucherStatusBadge from '@/components/debtAdjustmentVouchers/DebtAdjustmentVoucherStatusBadge';
import { ReceiptVoucherResponse } from '@/types/receiptVoucher';
import { DiscountVoucherUI } from '@/types/discountVoucher';
import { DebtAdjustmentVoucherResponse } from '@/types/debtAdjustmentVoucher';

// Format date to show only day-month-year
const formatDateOnly = (dateString: string): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return '-';
  }
};

interface CustomerDebtTabProps {
  customer: Customer | null;
  onDateRangeChange?: (dateFrom: string, dateTo: string) => void;
}

/**
 * CustomerDebtTab displays unified customer debt information using new API
 * Combines invoices and receipts in a single table with record_type field
 */
const CustomerDebtTab: React.FC<CustomerDebtTabProps> = ({
  customer,
  onDateRangeChange,
}) => {
  const { t } = useTranslation();
  const [pageSize] = useState(10);
  const [transactions, setTransactions] = useState<TransactionRecord[]>([]);
  const [loading, setLoading] = useState(false);

  const [total, setTotal] = useState(0);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showDiscountVoucherModal, setShowDiscountVoucherModal] = useState(false);
  const [showDebtAdjustmentModal, setShowDebtAdjustmentModal] = useState(false);

  // Confirmation modal states
  const [showConfirmReceiptModal, setShowConfirmReceiptModal] = useState(false);
  const [createdReceiptVoucher, setCreatedReceiptVoucher] = useState<ReceiptVoucherResponse | null>(null);
  const [showConfirmDiscountModal, setShowConfirmDiscountModal] = useState(false);
  const [createdDiscountVoucher, setCreatedDiscountVoucher] = useState<DiscountVoucherUI | null>(null);
  const [showConfirmDebtAdjustmentModal, setShowConfirmDebtAdjustmentModal] = useState(false);
  const [createdDebtAdjustmentVoucher, setCreatedDebtAdjustmentVoucher] = useState<DebtAdjustmentVoucherResponse | null>(null);

  // Page-based pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Balance information
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const [receiptAmount, setReceiptAmount] = useState(0);

  // Date range for filtering (default: current month)
  const [dateFrom, setDateFrom] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of month
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  });

  const [dateTo, setDateTo] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  });

  const handleDateRangeChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
    setPage(1); // Reset to first page
    onDateRangeChange?.(from, to);
  };

  // Notify parent of initial date range on mount
  useEffect(() => {
    onDateRangeChange?.(dateFrom, dateTo);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch debt report using new unified API
  useEffect(() => {
    const fetchDebtReport = async () => {
      if (!customer?.code) {
        setTransactions([]);
        return;
      }

      setLoading(true);
      try {
        // Fetch debt report with transactions
        const response = await customerApi.getCustomerDebtReport({
          dateFrom,
          dateTo,
          customerId: customer.code,
          page,
          pageSize,
        });

        setTransactions(response.data?.data || []);
        setTotal(response.data?.total || 0);
        setTotalPages(Math.ceil((response.data?.total || 0) / pageSize));

        // Fetch debt summary for balance information
        const summary = await customerApi.getCustomerDebtSummary({
          dateFrom,
          dateTo,
          customerId: customer.code,
        });

        setOpeningBalance(summary.opening_balance);
        setClosingBalance(summary.closing_balance);
        setInvoiceAmount(summary.invoice_amount);
        setReceiptAmount(summary.receipt_amount);
      } catch (err) {
        console.error('Error fetching debt report:', err);
        setTransactions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDebtReport();
  }, [customer?.code, page, pageSize, dateFrom, dateTo]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'waiting_payment':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLabels: Record<string, string> = {
      waiting_payment: t('invoices.status.waiting_payment', 'Waiting Payment'),
      completed: t('invoices.status.completed', 'Completed'),
      cancelled: t('invoices.status.cancelled', 'Cancelled'),
      draft: t('receiptVoucher.status.draft', 'Draft'),
      created: t('debtAdjustmentVoucher.status.created', 'Đã tạo'),
      confirmed: t('debtAdjustmentVoucher.status.confirmed', 'Đã xác nhận'),
    };
    return statusLabels[status] || status;
  };

  const columns: TableColumn<TransactionRecord>[] = [
    {
      key: 'code',
      title: t('customers.tabs.recordCode'),
      dataIndex: 'code',
      width: 150,
      sortable: true,
      render: (value) => (
        <div className="font-medium text-primary-600">
          {value}
        </div>
      ),
    },
    {
      key: 'record_type',
      title: t('customers.tabs.recordType'),
      dataIndex: 'record_type',
      width: 120,
      render: (value: string) => {
        if (value === 'invoice') {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {t('invoices.titleSingle')}
            </span>
          );
        } else if (value === 'receipt') {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              {t('receiptVoucher.titleSingle')}
            </span>
          );
        } else if (value === 'discount_voucher') {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              {t('discountVoucher.titleSingle')}
            </span>
          );
        } else if (value === 'debt_adjustment') {
          return (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {t('debtAdjustmentVoucher.titleSingle')}
            </span>
          );
        }
        return null;
      },
    },
    {
      key: 'date',
      title: t('customers.tabs.recordTime'),
      dataIndex: 'created_at',
      width: 120,
      sortable: true,
      render: (value, record) => {
        // For debt adjustments, use adjustment_date if available, otherwise created_at
        const dateValue = record.record_type === 'debt_adjustment' && (record as any).adjustment_date
          ? (record as any).adjustment_date
          : value;
        return (
          <div className="text-sm text-gray-900">{dateValue ? formatDateOnly(dateValue) : '-'}</div>
        );
      },
    },
    {
      key: 'status',
      title: t('common.status'),
      dataIndex: 'current_status',
      width: 120,
      align: 'center',
      render: (value: any, record: TransactionRecord) => {
        const status = value?.status || '';
        // Use DebtAdjustmentVoucherStatusBadge for debt adjustments
        if (record.record_type === 'debt_adjustment' && (status === 'created' || status === 'confirmed' || status === 'cancelled')) {
          return <DebtAdjustmentVoucherStatusBadge status={status as any} />;
        }
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(status)}`}>
            {getStatusLabel(status)}
          </span>
        );
      },
    },
    {
      key: 'amount',
      title: t('customers.tabs.amount'),
      dataIndex: 'total',
      width: 150,
      align: 'right',
      sortable: true,
      render: (_value, record) => {
        let amount: number = 0;
        if (record.record_type === 'invoice') {
          amount = record.total || 0;
        } else if (record.record_type === 'receipt') {
          amount = record.received_now || 0;
        } else if (record.record_type === 'discount_voucher') {
          amount = record.total_discount || 0;
        } else if (record.record_type === 'debt_adjustment') {
          amount = record.adjustment_amount || 0;
        }
        const isNegative = record.record_type === 'debt_adjustment' && amount < 0;
        return (
          <div className={`font-medium ${isNegative ? 'text-green-600' : record.record_type === 'debt_adjustment' ? 'text-red-600' : 'text-gray-900'}`}>
            {formatCurrency(amount)}
          </div>
        );
      },
    },
    {
      key: 'created_by_name',
      title: t('customers.tabs.creator'),
      dataIndex: 'created_by_name',
      width: 150,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value || '-'}
        </div>
      ),
    },
    {
      key: 'note',
      title: t('customers.tabs.note'),
      dataIndex: 'note',
      render: (value, record) => {
        // For debt adjustments, show reason in note column
        if (record.record_type === 'debt_adjustment' && record.reason) {
          return (
            <div className="text-sm text-gray-500 max-w-xs truncate" title={record.reason}>
              {record.reason}
            </div>
          );
        }
        return (
          <div className="text-sm text-gray-500 max-w-xs truncate">
            {value || '-'}
          </div>
        );
      },
    },
  ];

  // Handle next page
  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // Handle previous page
  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };



  if (loading && (!transactions || transactions.length === 0)) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date Range Picker */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <DateRangePicker
          dateFrom={dateFrom}
          dateTo={dateTo}
          onChange={handleDateRangeChange}
        />
      </div>

      {/* Summary for customer debt */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {formatCurrency(openingBalance)}
            </div>
            <div className="text-sm text-gray-500">
              {t('customers.debt.openingBalance')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(invoiceAmount)}
            </div>
            <div className="text-sm text-gray-500">
              {t('customers.debt.invoiceAmount')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(receiptAmount)}
            </div>
            <div className="text-sm text-gray-500">
              {t('customers.debt.receiptAmount')}
            </div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(closingBalance)}
            </div>
            <div className="text-sm text-gray-500">
              {t('customers.debt.closingBalance')}
            </div>
          </div>
        </div>
      </div>

      {/* Unified Transaction Table */}
      <div className="card">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-900">{t('customers.debt.transactions')}</h4>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowReceiptModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              {t('customers.debt.pay')}
            </button>
            <button
              onClick={() => setShowDiscountVoucherModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            >
              {t('discountVoucher.buttons.create', 'Tạo Phiếu Chiết khấu')}
            </button>
            <button
              onClick={() => setShowDebtAdjustmentModal(true)}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {t('debtAdjustmentVoucher.buttons.create')}
            </button>
          </div>
        </div>
        <Table
          columns={columns}
          data={transactions || []}
          loading={loading}
          rowKey="id"
          size="md"
          hoverable
          emptyText={t('customers.tabs.noDebtRecords')}
        />

        {/* Page-based Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              {t('common.total')}: {total} {t('customers.tabs.debtRecords')}
            </div>
            <div className="flex items-center gap-2">
              {/* Previous button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreviousPage}
                disabled={page === 1}
              >
                ‹ {t('common.previous', 'Previous')}
              </Button>

              {/* Page Numbers */}
              {totalPages > 1 && (
                <div className="flex gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded ${page === pageNum
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Next button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleNextPage}
                disabled={page >= totalPages}
              >
                {t('common.next', 'Next')} ›
              </Button>
            </div>
          </div>
        )}
      </div>



      {/* Modal tạo Phiếu thu từ công nợ khách hàng */}
      <CreateReceiptVoucherModal
        isOpen={showReceiptModal}
        onClose={() => setShowReceiptModal(false)}
        onCreated={(voucher) => {
          setCreatedReceiptVoucher(voucher);
          setShowReceiptModal(false);
          setShowConfirmReceiptModal(true);
        }}
        initialCustomer={customer ?? undefined}
      />

      {/* Modal xác nhận Phiếu thu */}
      <QuickConfirmReceiptVoucherModal
        isOpen={showConfirmReceiptModal}
        onClose={() => {
          setShowConfirmReceiptModal(false);
          setCreatedReceiptVoucher(null);
        }}
        onConfirmed={async () => {
          // Refresh debt report
          const response = await customerApi.getCustomerDebtReport({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
            page,
            pageSize,
          });
          setTransactions(response.data.data);
          setTotal(response.data.total);
          setTotalPages(Math.ceil(response.data.total / pageSize));

          const summary = await customerApi.getCustomerDebtSummary({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
          });
          setOpeningBalance(summary.opening_balance);
          setClosingBalance(summary.closing_balance);
          setInvoiceAmount(summary.invoice_amount);
          setReceiptAmount(summary.receipt_amount);
        }}
        onSkipped={async () => {
          // Refresh debt report
          const response = await customerApi.getCustomerDebtReport({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
            page,
            pageSize,
          });
          setTransactions(response.data.data);
          setTotal(response.data.total);
          setTotalPages(Math.ceil(response.data.total / pageSize));

          const summary = await customerApi.getCustomerDebtSummary({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
          });
          setOpeningBalance(summary.opening_balance);
          setClosingBalance(summary.closing_balance);
          setInvoiceAmount(summary.invoice_amount);
          setReceiptAmount(summary.receipt_amount);
        }}
        receiptVoucher={createdReceiptVoucher}
      />

      {/* Modal tạo Phiếu chiết khấu từ công nợ khách hàng */}
      <CreateDiscountVoucherModal
        isOpen={showDiscountVoucherModal}
        onClose={() => setShowDiscountVoucherModal(false)}
        onSuccess={async () => {
          setShowDiscountVoucherModal(false);
          // Refresh debt report
          const response = await customerApi.getCustomerDebtReport({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
            page,
            pageSize,
          });
          setTransactions(response.data.data);
          setTotal(response.data.total);
          setTotalPages(Math.ceil(response.data.total / pageSize));

          const summary = await customerApi.getCustomerDebtSummary({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
          });
          setOpeningBalance(summary.opening_balance);
          setClosingBalance(summary.closing_balance);
          setInvoiceAmount(summary.invoice_amount);
          setReceiptAmount(summary.receipt_amount);
        }}
        onCreated={(voucher) => {
          setCreatedDiscountVoucher(voucher);
          setShowDiscountVoucherModal(false);
          setShowConfirmDiscountModal(true);
        }}
        initialCustomer={customer ?? undefined}
      />

      {/* Modal xác nhận Phiếu chiết khấu */}
      <ConfirmDiscountVoucherModal
        isOpen={showConfirmDiscountModal}
        onClose={() => {
          setShowConfirmDiscountModal(false);
          setCreatedDiscountVoucher(null);
        }}
        onSuccess={async () => {
          // Refresh debt report
          const response = await customerApi.getCustomerDebtReport({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
            page,
            pageSize,
          });
          setTransactions(response.data.data);
          setTotal(response.data.total);
          setTotalPages(Math.ceil(response.data.total / pageSize));

          const summary = await customerApi.getCustomerDebtSummary({
            dateFrom,
            dateTo,
            customerId: customer?.code || '',
          });
          setOpeningBalance(summary.opening_balance);
          setClosingBalance(summary.closing_balance);
          setInvoiceAmount(summary.invoice_amount);
          setReceiptAmount(summary.receipt_amount);
        }}
        voucher={createdDiscountVoucher}
        action="confirm"
      />

      {/* Create Debt Adjustment Modal */}
      {customer && (
        <>
          <CreateDebtAdjustmentVoucherModal
            isOpen={showDebtAdjustmentModal}
            onClose={() => setShowDebtAdjustmentModal(false)}
            onSuccess={async () => {
              // Refresh debt report
              const response = await customerApi.getCustomerDebtReport({
                dateFrom,
                dateTo,
                customerId: customer.code,
                page,
                pageSize,
              });
              setTransactions(response.data.data);
              setTotal(response.data.total);
              setTotalPages(Math.ceil(response.data.total / pageSize));

              const summary = await customerApi.getCustomerDebtSummary({
                dateFrom,
                dateTo,
                customerId: customer.code,
              });
              setOpeningBalance(summary.opening_balance);
              setClosingBalance(summary.closing_balance);
              setInvoiceAmount(summary.invoice_amount);
              setReceiptAmount(summary.receipt_amount);
            }}
            onCreated={(voucher) => {
              setCreatedDebtAdjustmentVoucher(voucher);
              setShowDebtAdjustmentModal(false);
              setShowConfirmDebtAdjustmentModal(true);
            }}
            entityType="customer"
            entityId={customer.code}
            entityName={customer.name}
            reportPeriodFrom={dateFrom}
            reportPeriodTo={dateTo}
            currentDebt={closingBalance}
          />

          {/* Confirm Debt Adjustment Modal */}
          <ConfirmDebtAdjustmentVoucherModal
            isOpen={showConfirmDebtAdjustmentModal}
            onClose={() => {
              setShowConfirmDebtAdjustmentModal(false);
              setCreatedDebtAdjustmentVoucher(null);
            }}
            onSuccess={async () => {
              // Refresh debt report
              const response = await customerApi.getCustomerDebtReport({
                dateFrom,
                dateTo,
                customerId: customer.code,
                page,
                pageSize,
              });
              setTransactions(response.data.data);
              setTotal(response.data.total);
              setTotalPages(Math.ceil(response.data.total / pageSize));

              const summary = await customerApi.getCustomerDebtSummary({
                dateFrom,
                dateTo,
                customerId: customer.code,
              });
              setOpeningBalance(summary.opening_balance);
              setClosingBalance(summary.closing_balance);
              setInvoiceAmount(summary.invoice_amount);
              setReceiptAmount(summary.receipt_amount);
            }}
            voucher={createdDebtAdjustmentVoucher}
            action="confirm"
            currentDebt={closingBalance}
          />
        </>
      )}
    </div>
  );
};

export default CustomerDebtTab;
